import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { ImpactProviderCard } from "@app/projects/network-assessment/maps/network-coverage-map/components/ImpactProviderCard";
import { NetworkProviderCard } from "@app/projects/network-assessment/maps/network-coverage-map/components/NetworkProviderCard";
import { ProviderAccordionPaging } from "@app/projects/network-assessment/maps/network-coverage-map/components/ProviderAccordionPaging";
import { NetworkCoverageMapDataContext } from "@app/projects/network-assessment/maps/network-coverage-map/contexts/NetworkCoverageMapDataContext";
import { ProviderAccordionSelectedTab } from "@app/projects/network-assessment/maps/network-coverage-map/enumerators";
import { ProviderFetchMessage } from "@app/projects/network-assessment/maps/network-coverage-map/components/ProviderFetchMessage";
import { ProviderMapResults } from "@app/projects/results/common/mapping";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import React, { JSX, useContext, useEffect, useRef } from "react";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";

export const ProviderAccordionControlContent = ({ headerRef }: { headerRef: React.RefObject<HTMLDivElement> }): JSX.Element => {

  const mapDataContext = useContext(NetworkCoverageMapDataContext);
  const projectContext = useContext(CountyProjectContext);

  const providerCardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const providerCardAccordionRef = useRef<HTMLDivElement | null>(null);
  const boundaryAtSelectionRef = useRef<typeof mapDataContext.currentMapBoundary>(null);
  const scrolledProviderIdRef = useRef<string | null>(null);

  const setProviderCardRef = (id: string, node: HTMLDivElement | null) => {
    providerCardRefs.current[id] = node;
  };

  useEffect(() => {
    if (mapDataContext.visibleAccordionProviders?.filter(p => p.providerNetworkType !== ProviderNetworkTypeEnum.Network).length > 0) {
      let page = mapDataContext.impactProvidersPage;
      const totalPages = Math.ceil(mapDataContext.visibleAccordionProviders.filter(p => p.providerNetworkType !== ProviderNetworkTypeEnum.Network).length / mapDataContext.impactPageSize);

      // If the current page is greater than the total pages, reset to page 1
      // This can happen if the number of providers changes after sorting or filtering
      if (page > totalPages) {
        page = 1;
        mapDataContext.setImpactProvidersPage(1);
      }

      // Set the total pages for impact providers
      // This needs to be done after calculating the page to ensure it reflects the current state
      mapDataContext.setTotalImpactPages(totalPages);

      mapDataContext.setPagedImpactProviders(mapDataContext.visibleAccordionProviders.filter(p => p.providerNetworkType !== ProviderNetworkTypeEnum.Network)
        .slice((page - 1) * mapDataContext.impactPageSize, page * mapDataContext.impactPageSize)
      );
    }
    else {
      // If there are no impact providers, reset the paged impact providers to an empty array
      mapDataContext.setPagedImpactProviders([]);
      mapDataContext.setTotalImpactPages(0);
    }
  }, [
    mapDataContext.visibleAccordionProviders,
    mapDataContext.impactProvidersPage,
    mapDataContext.impactPageSize,
    mapDataContext.getSelectedSortValue()
  ]);

  useEffect(() => {
    if (mapDataContext.visibleAccordionProviders?.filter(p => p.providerNetworkType === ProviderNetworkTypeEnum.Network).length > 0) {
      let page = mapDataContext.networkProvidersPage;
      const totalPages = Math.ceil(mapDataContext.visibleAccordionProviders.filter(p => p.providerNetworkType === ProviderNetworkTypeEnum.Network).length / mapDataContext.networkPageSize);

      // If the current page is greater than the total pages, reset to page 1
      // This can happen if the number of providers changes after sorting or filtering
      if (page > totalPages) {
        page = 1;
        mapDataContext.setNetworkProvidersPage(1);
      }

      // Set the total pages for network providers
      // This needs to be done after calculating the page to ensure it reflects the current state
      mapDataContext.setTotalNetworkPages(totalPages);

      mapDataContext.setPagedNetworkProviders(mapDataContext.visibleAccordionProviders.filter(p => p.providerNetworkType === ProviderNetworkTypeEnum.Network)
        .slice((page - 1) * mapDataContext.networkPageSize, page * mapDataContext.networkPageSize)
      );
    }
    else {
      // If there are no network providers, reset the paged network providers to an empty array
      mapDataContext.setPagedNetworkProviders([]);
      mapDataContext.setTotalNetworkPages(0);
    }
  }, [
    mapDataContext.visibleAccordionProviders,
    mapDataContext.networkProvidersPage,
    mapDataContext.networkPageSize,
    mapDataContext.getSelectedSortValue()
  ]);

  // A new selection starts a pan; remember the view it started from so the scroll below can wait for the
  // list to be re-filtered when the map stops moving.
  useEffect(() => {
    boundaryAtSelectionRef.current = mapDataContext.currentMapBoundary;
    scrolledProviderIdRef.current = null;
  }, [mapDataContext.selectedProvider?.providerUniqueId]);

  // If a provider has been selected on the map, but the provider is not contained in the current
  // page of the visible accordion providers, we need to jump to the appropriate page.
  useEffect(() => {
    if (!mapDataContext.selectedProvider) return;

    if (mapDataContext.providerAccordionSelectedTab === ProviderAccordionSelectedTab.NetworkProviders) {
      const { page, position } = getPageAndPosition(
        mapDataContext.visibleAccordionProviders.filter(p => p.providerNetworkType === ProviderNetworkTypeEnum.Network),
        mapDataContext.networkPageSize
      );

      if (position < 0) return; // If the provider is not found, exit early
      mapDataContext.setNetworkProvidersPage(page);
    }
    else {
      const { page, position } = getPageAndPosition(
        mapDataContext.visibleAccordionProviders.filter(p => p.providerNetworkType !== ProviderNetworkTypeEnum.Network),
        mapDataContext.impactPageSize
      );

      if (position < 0) return; // If the provider is not found, exit early
      mapDataContext.setImpactProvidersPage(page);
    }
  }, [
    mapDataContext.selectedProvider,
    mapDataContext.providerAccordionSelectedTab,
    mapDataContext.visibleAccordionProviders,
  ]);

  // Runs after the page jump above has rendered the card. Waits for the first list update after the selection
  // (currentMapBoundary is read, not a dependency, so the paged lists changing is what re-runs it). Only the first
  // scroll per selection animates; later runs nudge the card back into view if the list pushed it out.
  useEffect(() => {
    const selectedId = mapDataContext.selectedProvider?.providerUniqueId;
    if (!selectedId) return;
    if (mapDataContext.currentMapBoundary === boundaryAtSelectionRef.current) return;

    const card = providerCardRefs.current[selectedId];
    if (!card) return;

    const isFirstScroll = scrolledProviderIdRef.current !== selectedId;
    scrolledProviderIdRef.current = selectedId;
    card.scrollIntoView?.({ behavior: isFirstScroll ? 'smooth' : 'auto', block: 'nearest' });
  }, [
    mapDataContext.selectedProvider,
    mapDataContext.providerAccordionSelectedTab,
    mapDataContext.pagedNetworkProviders,
    mapDataContext.pagedImpactProviders,
  ]);

  const getPageAndPosition = (
    providers: ProviderMapResults[],
    pageSize: number
  ): { page: number, position: number } => {
    let page = 0;
    let position = 0;

    position = providers.findIndex(p => p.providerUniqueId === mapDataContext.selectedProvider?.providerUniqueId);

    if (position < 0) {
      return { page: -1, position: -1 }; // If selected provider isn't found, exit early
    }

    if (position === 0) return { page: 1, position: 0 }; // Provider found at first position which means it is on the first page

    // Calculate the page as follows:
    // If the position is less than or equal to the page size, it is on the first page.
    // Otherwise, divide the position by the page size (using ceiling) to get the page number.
    // If the position is a multiple of the page size, we need to increment the page by 1
    // to account for the provider being at the top of the next page.
    page = position <= pageSize ? 1 : Math.ceil(position / pageSize);
    page = position % pageSize === 0 ? page + 1 : page;
    return { page, position };
  }

  return (
    <div className="network-coverage-map__provider-accordion-content" >
      <div style={{
        height: `calc(${mapDataContext.mapRef.current?.getCanvas().getBoundingClientRect().height}px - ${headerRef.current?.getBoundingClientRect().height + 105}px)`,
        overflow: 'auto',
      }}
        ref={providerCardAccordionRef}
      >
        {
          mapDataContext.loadingProviders
            ? <div className="h-100 d-flex justify-content-center align-items-center">
              <LoadingSpinner />
            </div>
            :
            <>
              <ProviderFetchMessage />
              {
                mapDataContext.providerAccordionSelectedTab === ProviderAccordionSelectedTab.OutOfNetworkProviders &&
                  mapDataContext.pagedImpactProviders &&
                  mapDataContext.pagedImpactProviders.length > 0
                  ? mapDataContext.pagedImpactProviders.map((p) =>
                    <div key={p.providerUniqueId} ref={node => setProviderCardRef(p.providerUniqueId, node)}>
                      <ImpactProviderCard
                        isSelected={!!mapDataContext.selectedProvider &&
                          p.providerUniqueId == mapDataContext.selectedProvider.providerUniqueId}
                        passPercentage={projectContext.adequacyResult?.details[0]?.accessSummary?.passPercentage || null}
                        provider={p}
                        onClick={mapDataContext.handleProviderCardClick} />
                    </div>)
                  : mapDataContext.providerAccordionSelectedTab === ProviderAccordionSelectedTab.NetworkProviders &&
                    mapDataContext.pagedNetworkProviders &&
                    mapDataContext.pagedNetworkProviders.length > 0
                    ? mapDataContext.pagedNetworkProviders.map((p) =>
                      <div key={p.providerUniqueId} ref={node => setProviderCardRef(p.providerUniqueId, node)}>
                        <NetworkProviderCard
                          isSelected={!!mapDataContext.selectedProvider &&
                            p.providerUniqueId == mapDataContext.selectedProvider.providerUniqueId}
                          provider={p}
                          onClick={mapDataContext.handleProviderCardClick} />
                      </div>)
                    : <></>
              }
            </>
        }
      </div>
      <div>
        <ProviderAccordionPaging accordionContainer={providerCardAccordionRef} />
      </div>
    </div>
  )
}